import { useCallback, useEffect, useRef, useState } from "react";
import {
  ACTIVE_MEDICAL_CASE_CLAUSE_LABEL,
  type ActiveMedicalCaseResponse,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import {
  fetchOverviewCompletionStatus,
  requestOverviewCompletion,
  UnauthorizedError,
} from "@/lib/clinician-api";
import {
  CompletionPicker,
  CompletionProgress,
  completionPayload,
  completionProblem,
  EMPTY_COMPLETION,
  type CompletionValue,
} from "./CompletionPicker";
import { useReadOnly } from "./SupportView";
import { WebdocControlStep } from "./WebdocControlStep";
import { labOrderOnlyControl, meetingNeedsLabOrder } from "@/lib/webdoc-step";

// TES-1169 (Emelie 2026-10-08, beslut 2) — "Begär komplettering" on the
// patient overview: during the consult, straight after it, or later while
// there is an active medical case. The ONE picker (TES-950) the meeting, the
// transfer review and the monthly review use; the server opens exactly the
// CP-06 flow the meeting's "Komplettering behövs" opens.
//
// Whether there is an active medical case is the server's answer
// (activeMedicalCase in contracts); without one the button stays disabled
// with the reason it gives. The server refuses the request on the same rule.
//
// Doctor only, outside the admin's read-only view, like "Skicka besked".
// The staff-facing strings are proposed copy.
export function OverviewCompletionRequest({
  patientId,
  onUnauthorized,
  startOpen = false,
  onSent,
}: {
  patientId: string;
  onUnauthorized: () => void;
  // TES-1186 — opened from the treatment decision's "Begär ytterligare
  // komplettering": the picker shows at once, and the decision closes once
  // the request is sent.
  startOpen?: boolean;
  onSent?: () => void;
}) {
  const { claims } = useAuth();
  const readOnly = useReadOnly();
  const [status, setStatus] = useState<ActiveMedicalCaseResponse | null>(null);
  const [statusError, setStatusError] = useState(false);
  const [open, setOpen] = useState(startOpen);
  const [value, setValue] = useState<CompletionValue>({
    ...EMPTY_COMPLETION,
    outcome: "completion",
  });
  // TES-1181 — "Provbeställning/remiss för blodprov är skapad i Webdoc",
  // required before a new blood test reaches her.
  const [labOrderDone, setLabOrderDone] = useState(false);
  const [requisitionRef, setRequisitionRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;
  const isDoctor = claims?.role === "doctor" && !readOnly;

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await fetchOverviewCompletionStatus(patientId));
      setStatusError(false);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorizedRef.current();
        return;
      }
      setStatusError(true);
    }
  }, [patientId]);

  useEffect(() => {
    if (isDoctor) void loadStatus();
  }, [isDoctor, loadStatus]);

  if (!isDoctor) return null;

  const needsLabOrder = meetingNeedsLabOrder(value);
  const problem =
    completionProblem(value) ??
    (needsLabOrder && !labOrderDone
      ? "Bekräfta att provbeställningen/remissen är skapad i Webdoc."
      : null);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const webdocControl = labOrderOnlyControl({
        needsLabOrder,
        labOrderDone,
        requisitionRef,
      });
      await requestOverviewCompletion(patientId, {
        ...completionPayload(value),
        ...(webdocControl ? { webdocControl } : {}),
      });
      setSent(true);
      setOpen(false);
      onSent?.();
      setValue({ ...EMPTY_COMPLETION, outcome: "completion" });
      setLabOrderDone(false);
      setRequisitionRef("");
      setReloadKey((k) => k + 1);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      if (e instanceof Error && e.message === "no_active_medical_case") {
        // The case closed since the page loaded: show why.
        setOpen(false);
        await loadStatus();
        return;
      }
      setError("Kompletteringen kunde inte skickas. Försök igen.");
    } finally {
      setBusy(false);
    }
  }

  const active = status?.active === true;
  const disabledReason =
    status && !status.active
      ? status.message
      : statusError
        ? "Det gick inte att avgöra om det finns ett aktivt medicinskt ärende. Ladda om sidan."
        : null;

  return (
    <div className="flex flex-col gap-xs text-sm">
      <CompletionProgress
        patientId={patientId}
        reloadKey={reloadKey}
        onUnauthorized={onUnauthorized}
      />
      {sent ? (
        <p className="text-xs text-textSecondary" role="status">
          Kompletteringen är skickad. Patienten ser den som en uppgift i appen.
        </p>
      ) : null}
      {open && active ? (
        <div className="flex flex-col gap-sm rounded-md border border-surfaceMuted p-sm">
          <CompletionPicker
            patientId={patientId}
            value={value}
            onChange={setValue}
            showOutcome={false}
            disabled={busy}
            onUnauthorized={onUnauthorized}
          />
          <WebdocControlStep
            idPrefix="overview-completion"
            labOrder={needsLabOrder}
            labOrderDone={labOrderDone}
            onLabOrderDoneChange={setLabOrderDone}
            requisitionRef={{
              value: requisitionRef,
              onChange: setRequisitionRef,
            }}
            disabled={busy}
          />
          <div className="flex flex-wrap items-center gap-sm">
            <button
              type="button"
              onClick={() => void send()}
              disabled={busy || problem !== null}
              title={problem ?? undefined}
              className="rounded-md bg-brand px-md py-xs text-sm text-white hover:opacity-90 disabled:opacity-50"
            >
              Skicka komplettering till patienten
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={busy}
              className="text-sm text-textSecondary underline"
            >
              Avbryt
            </button>
          </div>
          {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
      ) : (
        <div className="flex flex-col gap-xs">
          <button
            type="button"
            onClick={() => {
              setSent(false);
              setOpen(true);
            }}
            disabled={!active}
            aria-describedby={
              disabledReason ? "overview-completion-reason" : undefined
            }
            className="self-start rounded-md border border-surfaceMuted px-md py-xs text-sm text-textPrimary hover:border-brand disabled:cursor-not-allowed disabled:opacity-50"
          >
            Begär komplettering
          </button>
          {disabledReason ? (
            <p
              id="overview-completion-reason"
              className="text-xs text-textSecondary"
            >
              {disabledReason}
            </p>
          ) : status?.active ? (
            <p className="text-xs text-textSecondary">
              {`Aktivt ärende: ${status.clauses
                .map((c) => ACTIVE_MEDICAL_CASE_CLAUSE_LABEL[c])
                .join(", ")}.`}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
